import { useState } from "react";
import { toast } from "sonner";
import { submitToolkitRequest, createCheckout } from "../lib/api";
import {
  BookOpen, Users, Users2, Video, Notebook, Award,
  ShieldCheck, HandCoins, Target, Play, Loader2, Sparkles, Send,
} from "lucide-react";

const INCLUDES = [
  { icon: BookOpen, label: "Lesson Guide" },
  { icon: Users, label: "Student Workbook" },
  { icon: Users2, label: "Parent Discussion Guide" },
  { icon: Video, label: "Teaching Videos" },
  { icon: Notebook, label: "Reflection Journal" },
  { icon: Award, label: "Certificate of Completion" },
];

const BENEFITS = [
  { icon: ShieldCheck, title: "Reduce Youth Crime", body: "Make better choices. Protect your future." },
  { icon: HandCoins, title: "Build Stronger Communities", body: "Stronger families. Stronger neighborhoods." },
  { icon: Target, title: "Empower Young Men", body: "Purpose over pressure. Legacy over likes." },
];

const inputCls = "w-full rounded-md border border-black/15 bg-white px-4 py-3 outline-none focus:border-[#6B21A8] text-sm";
const inputDarkCls = "w-full rounded-md border border-white/20 bg-white/5 px-4 py-3 outline-none focus:border-[#F5B90A] text-sm text-white placeholder:text-white/50";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR"];

function RequestForm() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", org_type: "", city: "", state: "",
    zip_code: "", country: "USA", audience_size: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToolkitRequest(f);
      toast.success("Request received! Coach Kaf's team will reach out within 3 business days.");
      setF({ name: "", email: "", phone: "", org_type: "", city: "", state: "", zip_code: "", country: "USA", audience_size: "", notes: "" });
    } catch { toast.error("Submission failed. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="toolkit-request-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><span className="text-sm font-medium text-black/80">Full name *</span><input required className={inputCls + " mt-1"} value={f.name} onChange={set("name")} data-testid="tk-name" /></label>
        <label className="block"><span className="text-sm font-medium text-black/80">Email *</span><input required type="email" className={inputCls + " mt-1"} value={f.email} onChange={set("email")} data-testid="tk-email" /></label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block"><span className="text-sm font-medium text-black/80">Phone</span><input className={inputCls + " mt-1"} value={f.phone} onChange={set("phone")} data-testid="tk-phone" /></label>
        <label className="block">
          <span className="text-sm font-medium text-black/80">I'm a…</span>
          <select className={inputCls + " mt-1"} value={f.org_type} onChange={set("org_type")} data-testid="tk-org">
            <option value="">Select one…</option>
            <option value="parent">Parent / Guardian</option>
            <option value="mentor">Mentor</option>
            <option value="school">School / Educator</option>
            <option value="church">Church / Faith Leader</option>
            <option value="community">Community / Nonprofit</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block"><span className="text-sm font-medium text-black/80">City</span><input className={inputCls + " mt-1"} value={f.city} onChange={set("city")} data-testid="tk-city" /></label>
        <label className="block">
          <span className="text-sm font-medium text-black/80">State</span>
          <select className={inputCls + " mt-1"} value={f.state} onChange={set("state")} data-testid="tk-state">
            <option value="">—</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block"><span className="text-sm font-medium text-black/80">ZIP</span><input className={inputCls + " mt-1"} value={f.zip_code} onChange={set("zip_code")} data-testid="tk-zip" /></label>
      </div>
      <label className="block">
        <span className="text-sm font-medium text-black/80">How many young men will you use this with?</span>
        <input className={inputCls + " mt-1"} value={f.audience_size} onChange={set("audience_size")} data-testid="tk-audience" placeholder="e.g. 1 son at home, a class of 25, our whole youth group…" />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-black/80">Anything else?</span>
        <textarea rows="3" className={inputCls + " mt-1"} value={f.notes} onChange={set("notes")} data-testid="tk-notes" placeholder="Tell us what you're hoping to see change." />
      </label>
      <button type="submit" disabled={loading} data-testid="tk-submit" className="btn-primary w-full">
        {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>) : (<><Send className="mr-2 h-4 w-4" /> Request the Toolkit</>)}
      </button>
      <p className="text-xs text-black/50 text-center">Free to request. Available nationwide. We'll follow up personally.</p>
    </form>
  );
}

export default function Turnaround() {
  const [buying, setBuying] = useState(false);

  const buy = async () => {
    setBuying(true);
    try {
      const { checkout_url } = await createCheckout({
        lookup_key: "turnaround_toolkit",
        amount: 49,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Try again in a moment.");
      setBuying(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="turnaround-hero">
        <div className="noise" />
        <div className="absolute inset-0 opacity-25">
          <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/40 via-transparent to-[#F5B90A]/20" />
        </div>
        <div className="container-x relative py-20 md:py-28 grid gap-10 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Change Center Mentoring presents</span>
            </div>
            <h1 className="mt-6 font-display text-6xl md:text-8xl font-black tracking-tight leading-[0.95]">
              THE <span className="text-[#F5B90A]">TURNAROUND</span><br />PROJECT
            </h1>
            <p className="mt-6 max-w-xl text-xl md:text-2xl text-white/85 leading-snug">
              <span className="font-semibold">Stop the chase</span> — <span className="italic text-[#F5B90A]">before</span> it starts.
            </p>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed">
              A powerful video series and mentoring program teaching young men the real consequences of their choices — and how to build a better future. Available nationwide.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#request" className="btn-gold" data-testid="hero-request-btn">
                <Send className="mr-2 h-4 w-4" /> Request the Toolkit (free)
              </a>
              <button onClick={buy} disabled={buying} className="btn-outline-light" data-testid="hero-buy-btn">
                {buying ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…</>) : (<><Sparkles className="mr-2 h-4 w-4" /> Purchase — $49</>)}
              </button>
            </div>
          </div>
          <div className="md:col-span-5 flex justify-center md:justify-end relative">
            <div className="absolute inset-6 bg-[#6B21A8]/25 rounded-full blur-3xl" />
            <img
              src="/coach-kaf-hero.png"
              alt="Coach Kaf"
              className="relative h-[440px] md:h-[560px] w-auto drop-shadow-[0_25px_50px_rgba(107,33,168,0.55)]"
            />
            <span className="absolute bottom-6 left-6 md:left-auto md:right-6 bg-[#6B21A8]/90 backdrop-blur-sm px-4 py-2 border border-[#F5B90A]/60">
              <p className="font-display text-xl font-bold leading-none">COACH KAF</p>
              <p className="text-[10px] uppercase tracking-widest text-[#F5B90A]">Mentor · Leader · Game Changer</p>
            </span>
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="bg-[#4A154B] text-white">
        <div className="container-x py-16 md:py-20">
          <div className="grid gap-10 md:grid-cols-12 items-center">
            <div className="md:col-span-5">
              <span className="overline text-[#F5B90A]">Watch the launch</span>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">See what The Turnaround Project is about.</h2>
              <p className="mt-4 text-white/75">Coach Kaf walks you through the vision, the story, and why this generation needs a turnaround — right now.</p>
            </div>
            <div className="md:col-span-7">
              <div className="relative border-2 border-[#F5B90A] overflow-hidden bg-black" data-testid="turnaround-video">
                <video
                  className="w-full h-auto"
                  controls
                  playsInline
                  poster="/photos/turnaround-poster.jpg"
                  preload="metadata"
                >
                  <source src="/videos/turnaround-promo.mp4" type="video/mp4" />
                  Your browser does not support embedded video.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INCLUDES */}
      <section className="bg-white border-b border-black/10">
        <div className="container-x py-16 md:py-24">
          <div className="max-w-2xl mb-10">
            <span className="overline text-[#6B21A8]">What's inside</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">The Turnaround Toolkit includes:</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="toolkit-includes">
            {INCLUDES.map((it) => (
              <div key={it.label} className="border border-black/10 p-6 flex flex-col items-center text-center hover:shadow-[4px_4px_0_#0A0A0A] transition-shadow">
                <div className="h-14 w-14 rounded-full bg-[#6B21A8]/10 flex items-center justify-center mb-4">
                  <it.icon className="h-7 w-7 text-[#6B21A8]" strokeWidth={1.75} />
                </div>
                <p className="text-sm font-semibold leading-tight">{it.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="noise" />
        <div className="container-x relative py-16 md:py-24">
          <div className="grid gap-6 md:grid-cols-3" data-testid="toolkit-benefits">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className={`border p-8 ${i === 1 ? "border-[#F5B90A] bg-[#F5B90A]/5" : "border-white/15"}`}>
                <b.icon className={`h-9 w-9 ${i === 1 ? "text-[#F5B90A]" : "text-[#F5B90A]/80"}`} strokeWidth={1.75} />
                <h3 className="mt-6 font-display text-2xl font-bold">{b.title}</h3>
                <p className="mt-3 text-white/70 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REQUEST + BUY */}
      <section id="request" className="bg-[#FDFBF7]">
        <div className="container-x py-16 md:py-24 grid gap-10 md:grid-cols-12 items-start">
          <div className="md:col-span-5">
            <span className="overline text-[#6B21A8]">Get the toolkit</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">Make the <span className="text-[#6B21A8]">change.</span></h2>
            <p className="mt-6 text-black/70 leading-relaxed">Two ways to get started. Request a free evaluation copy from anywhere in the country — or grab the full toolkit today for $49 and start now.</p>

            <div className="mt-8 border-2 border-[#0A0A0A] bg-white p-6" data-testid="toolkit-buy-card">
              <div className="flex items-baseline gap-3">
                <p className="font-display text-6xl font-black leading-none">$49</p>
                <p className="text-sm text-black/60">one-time · instant access</p>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-black/80">
                <li className="flex gap-2"><Play className="h-4 w-4 text-[#6B21A8] mt-0.5" /> All 6 toolkit resources</li>
                <li className="flex gap-2"><Play className="h-4 w-4 text-[#6B21A8] mt-0.5" /> Full teaching video series</li>
                <li className="flex gap-2"><Play className="h-4 w-4 text-[#6B21A8] mt-0.5" /> Certificate of Completion</li>
              </ul>
              <button onClick={buy} disabled={buying} className="btn-primary w-full mt-6" data-testid="buy-btn">
                {buying ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…</>) : (<><Sparkles className="mr-2 h-4 w-4" /> Purchase for $49</>)}
              </button>
              <p className="mt-3 text-xs text-black/50 text-center">Secure checkout via Stripe. Access details emailed within 24 hours.</p>
            </div>
          </div>

          <div className="md:col-span-7 bg-white border border-black/10 p-6 md:p-8">
            <span className="overline text-[#6B21A8]">Or — request it free</span>
            <h3 className="mt-3 font-display text-3xl font-bold leading-tight">For parents, mentors, schools & churches nationwide.</h3>
            <p className="mt-3 text-sm text-black/70">Fill this out and Coach Kaf's team will personally follow up with the toolkit and next steps.</p>
            <div className="mt-6">
              <RequestForm />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-[#F5B90A]">
        <div className="container-x py-16 md:py-20 grid gap-6 md:grid-cols-12 items-center">
          <div className="md:col-span-8">
            <p className="overline text-black/70">Change is possible.</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-black leading-tight">
              Your turnaround <span className="italic">starts now.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a href="#request" className="btn-primary" data-testid="footer-request-btn">Request the toolkit</a>
          </div>
        </div>
      </section>
    </>
  );
}
